/**
 * Unicorn Lab Service Cost & Profit Calculator — Calculation Engine
 * Pure functions only — no DOM dependency.
 * Reusable for future Lite/Pro platform.
 * unicornlab.com.au
 */

'use strict';

// ─── PRODUCT DEFAULTS REGISTRY ──────────────────────────────────────────────

const UL_PRODUCT_DEFAULTS = {
  gel_polish_10ml:    { name: 'Gel Polish (10ml)',        size: 10,  unit: 'ml', apps: 35,  mlPerNail: 0.15 },
  gel_polish_15ml:    { name: 'Gel Polish (15ml)',        size: 15,  unit: 'ml', apps: 52,  mlPerNail: 0.15 },
  biab_15ml:          { name: 'BIAB / Builder Gel (15ml)',size: 15,  unit: 'ml', apps: 30,  mlPerNail: 0.15 },
  biab_30g:           { name: 'BIAB / Builder Gel (30g)', size: 30,  unit: 'g',  apps: 50,  mlPerNail: null },
  base_coat_10ml:     { name: 'Base Coat (10ml)',         size: 10,  unit: 'ml', apps: 40,  mlPerNail: 0.12 },
  top_coat_10ml:      { name: 'Top Coat (10ml)',          size: 10,  unit: 'ml', apps: 35,  mlPerNail: 0.15 },
  rubber_base_15ml:   { name: 'Rubber Base (15ml)',       size: 15,  unit: 'ml', apps: 30,  mlPerNail: 0.15 },
  hard_gel_15ml:      { name: 'Hard Gel (15ml)',          size: 15,  unit: 'ml', apps: 20,  mlPerNail: null },
  polygel_30g:        { name: 'Poly Gel (30g)',           size: 30,  unit: 'g',  apps: 25,  mlPerNail: null },
  primer_15ml:        { name: 'Primer (15ml)',            size: 15,  unit: 'ml', apps: 300, mlPerNail: null, flat: true },
  dehydrator_10ml:    { name: 'Dehydrator (10ml)',        size: 10,  unit: 'ml', apps: 80,  mlPerNail: null, flat: true },
  acrylic_30g:        { name: 'Acrylic Powder (30g)',     size: 30,  unit: 'g',  apps_full: 20, apps_infill: 40 },
  acrylic_250g:       { name: 'Acrylic Powder (250g)',    size: 250, unit: 'g',  apps_full: 166, apps_infill: 333 },
  acrylic_500g:       { name: 'Acrylic Powder (500g)',    size: 500, unit: 'g',  apps_full: 333, apps_infill: 666 },
  monomer_50ml:       { name: 'Monomer (50ml)',           size: 50,  unit: 'ml', apps: 15,  mlPerNail: null },
  monomer_100ml:      { name: 'Monomer (100ml)',          size: 100, unit: 'ml', apps: 30,  mlPerNail: null },
  monomer_250ml:      { name: 'Monomer (250ml)',          size: 250, unit: 'ml', apps: 75,  mlPerNail: null },
  soft_gel_tips_500:  { name: 'Soft Gel Tips (500 pack)', size: 500, unit: 'tips', apps: 40, perSet: 10 },
  acetone_500ml:      { name: 'Acetone (500ml)',          size: 500, unit: 'ml', apps: 60,  mlPerNail: null },
  nail_glue_3g:       { name: 'Nail Glue (3g)',           size: 3,   unit: 'g',  apps: 50,  mlPerNail: null },
  cuticle_oil:        { name: 'Cuticle Oil',              mlPerService: 0.5 },
  // Nail art defaults
  rhinestones:        { name: 'Rhinestones',              packQty: 100, packPrice: 2.00  },
  swarovski:          { name: 'Swarovski Crystals',       packQty: 50,  packPrice: 9.00  },
  waterslides:        { name: 'Waterslide Decals',        packQty: 12,  packPrice: 4.00  },
  glitter_10g:        { name: 'Glitter (10g jar)',        packSize: 10, packPrice: 4.00, usedPerService: 0.8 },
  // Consumables
  file_buffer:        { name: 'File / Buffer',            packQty: 50,  packPrice: 12.00 },
  wipes:              { name: 'Lint-free Wipes',          packQty: 200, packPrice: 8.00  },
  gloves:             { name: 'Gloves (pair)',            packQty: 100, packPrice: 15.00 },
  nail_forms:         { name: 'Nail Forms',               packQty: 500, packPrice: 18.00, perService: 10 },
};

// ─── NAIL LENGTH MULTIPLIERS ─────────────────────────────────────────────────

const UL_LENGTH_MULTIPLIER = {
  natural: 1.0,
  mid:     1.5,
  long:    2.0,
};

// ─── SERVICE PRESETS (suggested product categories only) ─────────────────────

const UL_SERVICE_PRESETS = {
  gel_polish:        { name: 'Gel Polish Manicure',       categories: ['gel_system', 'consumables'] },
  biab_overlay:      { name: 'BIAB Overlay',              categories: ['biab_system', 'consumables'] },
  biab_infill:       { name: 'BIAB Infill',               categories: ['biab_system', 'consumables'] },
  acrylic_full:      { name: 'Acrylic Full Set',          categories: ['acrylic_system', 'consumables'] },
  acrylic_infill:    { name: 'Acrylic Infill',            categories: ['acrylic_system', 'consumables'] },
  gel_extensions:    { name: 'Gel / Gel-X Extensions',   categories: ['gel_system', 'tips', 'consumables'] },
  builder_extensions:{ name: 'Builder Gel Extensions',   categories: ['biab_system', 'tips', 'consumables'] },
  removal:           { name: 'Removal',                   categories: ['removal', 'consumables'] },
  manicure:          { name: 'Manicure',                  categories: ['consumables'] },
  nail_repair:       { name: 'Nail Repair',               categories: ['gel_system', 'consumables'] },
  nail_art_addon:    { name: 'Nail Art Add-on',           categories: ['nail_art'] },
  custom:            { name: 'Custom Service',            categories: [] },
};

// ─── CORE CALCULATION FUNCTIONS ───────────────────────────────────────────────

/**
 * Method 1: Cost by measured quantity (ml or g)
 * @param {number} purchasePrice  - price paid for the container
 * @param {number} containerSize  - size in ml or g
 * @param {number} quantityUsed   - ml/g used per service (before wastage)
 * @param {number} wastage        - decimal e.g. 0.05 for 5%
 * @returns {number} cost per service
 */
function calcCostByMeasurement(purchasePrice, containerSize, quantityUsed, wastage = 0.05) {
  if (!containerSize || containerSize <= 0) return 0;
  const pricePerUnit = purchasePrice / containerSize;
  return pricePerUnit * quantityUsed * (1 + wastage);
}

/**
 * Method 2: Cost by estimated applications
 * @param {number} purchasePrice     - price paid
 * @param {number} estimatedApps     - estimated number of services per container
 * @param {number} wastage           - decimal e.g. 0.05
 * @returns {number} cost per service
 */
function calcCostByApplications(purchasePrice, estimatedApps, wastage = 0.05) {
  if (!estimatedApps || estimatedApps <= 0) return 0;
  return (purchasePrice / estimatedApps) * (1 + wastage);
}

/**
 * Calculate ml used per service using the per-nail formula
 * @param {number} mlPerNail      - ml per nail (default 0.15)
 * @param {number} nails          - number of nails (default 10)
 * @param {number} coats          - number of coats applied
 * @param {string} nailLength     - 'natural' | 'mid' | 'long'
 * @returns {number} total ml used
 */
function calcMlUsed(mlPerNail = 0.15, nails = 10, coats = 1, nailLength = 'natural') {
  const multiplier = UL_LENGTH_MULTIPLIER[nailLength] || 1.0;
  return mlPerNail * nails * coats * multiplier;
}

/**
 * Per-piece product cost (rhinestones, crystals, tips, etc.)
 * @param {number} packPrice  - total pack price
 * @param {number} packQty    - pieces in pack
 * @param {number} usedQty    - pieces used in service
 * @param {number} wastage    - decimal
 * @returns {number} cost per service
 */
function calcCostPerPiece(packPrice, packQty, usedQty, wastage = 0.05) {
  if (!packQty || packQty <= 0) return 0;
  const pricePerPiece = packPrice / packQty;
  return pricePerPiece * usedQty * (1 + wastage);
}

/**
 * Sheet-based product cost (waterslides, foils, stickers)
 * @param {number} sheetPrice     - total sheet/pack price
 * @param {number} designsPerPack - usable designs in pack
 * @param {number} designsUsed    - designs used in service
 * @returns {number} cost per service
 */
function calcCostBySheet(sheetPrice, designsPerPack, designsUsed) {
  if (!designsPerPack || designsPerPack <= 0) return 0;
  return (sheetPrice / designsPerPack) * designsUsed;
}

/**
 * Glitter cost by weight
 * @param {number} packPrice   - jar price
 * @param {number} packSize_g  - jar size in grams
 * @param {number} usedGrams   - grams used in service
 * @param {number} wastage     - decimal
 * @returns {number} cost per service
 */
function calcGlitterCost(packPrice, packSize_g, usedGrams = 0.8, wastage = 0.05) {
  if (!packSize_g || packSize_g <= 0) return 0;
  return (packPrice / packSize_g) * usedGrams * (1 + wastage);
}

/**
 * Weight-per-nail product cost (pigment, chrome powder)
 * @param {number} packPrice    - pack/jar price
 * @param {number} packSize_g   - jar size in grams
 * @param {number} nailsUsed    - number of nails
 * @param {number} gPerNail     - grams used per nail (default 0.01)
 * @returns {number} cost per service
 */
function calcWeightPerNailCost(packPrice, packSize_g, nailsUsed = 10, gPerNail = 0.01) {
  if (!packSize_g || packSize_g <= 0) return 0;
  return (packPrice / packSize_g) * nailsUsed * gPerNail;
}

/**
 * Cuticle oil cost per service
 * @param {number} bottlePrice  - bottle price
 * @param {number} bottleSize   - bottle size in ml
 * @param {number} mlPerService - ml used per service (default 0.5)
 * @returns {number} cost per service
 */
function calcCuticleOilCost(bottlePrice, bottleSize, mlPerService = 0.5) {
  if (!bottleSize || bottleSize <= 0) return 0;
  return (bottlePrice / bottleSize) * mlPerService;
}

/**
 * Labour cost
 * @param {number} totalMinutes  - total service time in minutes
 * @param {number} hourlyRate    - target hourly rate in $
 * @returns {number} labour cost
 */
function calcLabourCost(totalMinutes, hourlyRate) {
  if (!hourlyRate || hourlyRate <= 0) return 0;
  return (totalMinutes / 60) * hourlyRate;
}

/**
 * Variable fee cost
 * @param {number} servicePrice        - current service price
 * @param {number} cardFeePercent      - decimal e.g. 0.017
 * @param {number} fixedTransactionFee - e.g. 0.30
 * @param {number} bookingFeePercent   - decimal e.g. 0.02
 * @returns {number} total variable fees
 */
function calcVariableFees(servicePrice, cardFeePercent = 0, fixedTransactionFee = 0, bookingFeePercent = 0) {
  const cardFee    = servicePrice * cardFeePercent;
  const bookingFee = servicePrice * bookingFeePercent;
  return cardFee + fixedTransactionFee + bookingFee;
}

/**
 * Master service cost calculator
 * @param {Object} inputs
 * @returns {Object} full result set
 */
function calcServiceResults(inputs) {
  const {
    servicePrice       = 0,
    productRows        = [],   // array of { cost: number, label: string, category: string }
    labourMinutes      = 0,
    hourlyRate         = 0,
    cardFeePercent     = 0,
    fixedTransactionFee = 0,
    bookingFeePercent  = 0,
    targetMarginPercent = 40,
  } = inputs;

  // Sum product rows
  const productCostBreakdown = {
    nail_system:   0,
    nail_art:      0,
    consumables:   0,
    fees:          0,
  };

  let totalProductCost = 0;
  for (const row of productRows) {
    const cost = row.cost || 0;
    totalProductCost += cost;
    if (row.category === 'nail_art')    productCostBreakdown.nail_art   += cost;
    else if (row.category === 'consumables') productCostBreakdown.consumables += cost;
    else                                 productCostBreakdown.nail_system += cost;
  }

  const labourCost    = calcLabourCost(labourMinutes, hourlyRate);
  const variableFees  = calcVariableFees(servicePrice, cardFeePercent, fixedTransactionFee, bookingFeePercent);
  productCostBreakdown.fees = variableFees;

  // Without labour
  const directCostNoLabour   = totalProductCost + variableFees;
  const grossProfitNoLabour  = servicePrice - directCostNoLabour;
  const grossMarginNoLabour  = servicePrice > 0 ? (grossProfitNoLabour / servicePrice) * 100 : 0;
  const recPriceNoLabour     = directCostNoLabour > 0 ? directCostNoLabour / (1 - targetMarginPercent / 100) : 0;

  // With labour
  const directCostWithLabour  = totalProductCost + labourCost + variableFees;
  const grossProfitWithLabour = servicePrice - directCostWithLabour;
  const grossMarginWithLabour = servicePrice > 0 ? (grossProfitWithLabour / servicePrice) * 100 : 0;
  const effectiveHourly       = labourMinutes > 0 ? grossProfitWithLabour / (labourMinutes / 60) : null;
  const recPriceWithLabour    = directCostWithLabour > 0 ? directCostWithLabour / (1 - targetMarginPercent / 100) : 0;

  const diagnostic = getDiagnostic({
    grossProfitNoLabour,
    effectiveHourly,
    hourlyRate,
    totalProductCost,
    labourMinutes,
    servicePrice,
    productCostBreakdown,
  });

  return {
    // Inputs echo
    servicePrice,
    labourMinutes,
    hourlyRate,
    targetMarginPercent,
    // Cost breakdown
    totalProductCost,
    labourCost,
    variableFees,
    productCostBreakdown,
    // Without labour
    directCostNoLabour,
    grossProfitNoLabour,
    grossMarginNoLabour,
    recPriceNoLabour,
    // With labour
    directCostWithLabour,
    grossProfitWithLabour,
    grossMarginWithLabour,
    effectiveHourly,
    recPriceWithLabour,
    // Meta
    diagnostic,
    priceIncreaseRequired: Math.max(0, recPriceWithLabour - servicePrice),
  };
}

// ─── DIAGNOSTIC CLASSIFIER ────────────────────────────────────────────────────

function getDiagnostic({ grossProfitNoLabour, effectiveHourly, hourlyRate, totalProductCost, labourMinutes, servicePrice, productCostBreakdown }) {
  if (servicePrice <= 0) return null;

  const artShare = productCostBreakdown.nail_art / (totalProductCost || 1);

  if (grossProfitNoLabour < 0) {
    return {
      type: 'loss',
      icon: '⚠️',
      headline: 'This service costs more to deliver than you currently charge.',
      detail: 'Your product costs and fees exceed your service price. A price increase is needed before this service becomes profitable.',
      colour: '#fee2e2',
      textColour: '#991b1b',
    };
  }

  if (hourlyRate > 0 && effectiveHourly !== null && effectiveHourly < hourlyRate) {
    return {
      type: 'underpriced',
      icon: '💛',
      headline: 'Products are covered, but your price doesn\'t fully reflect your time.',
      detail: `You are taking home $${effectiveHourly.toFixed(2)}/hr, which is below your target of $${hourlyRate.toFixed(2)}/hr.`,
      colour: '#fef9c3',
      textColour: '#854d0e',
    };
  }

  if (artShare > 0.5) {
    return {
      type: 'high_material',
      icon: '💎',
      headline: 'Nail-art materials make up a large share of this service cost.',
      detail: 'Consider reviewing your embellishment add-on charge separately.',
      colour: '#f3e8ff',
      textColour: '#6b21a8',
    };
  }

  if (labourMinutes > 0 && hourlyRate > 0 && ((labourMinutes / 60) * hourlyRate > totalProductCost * 3)) {
    return {
      type: 'time_heavy',
      icon: '⏱️',
      headline: 'Product cost is low, but your time is the main cost driver for this service.',
      detail: 'Consider whether your current price reflects the time you invest.',
      colour: '#dbeafe',
      textColour: '#1e40af',
    };
  }

  if (hourlyRate > 0) {
    return {
      type: 'profitable',
      icon: '✅',
      headline: 'This service covers its costs and hourly rate target.',
      detail: 'Looking good! Your price covers products and meets your target hourly rate.',
      colour: '#dcfce7',
      textColour: '#14532d',
    };
  }

  return {
    type: 'profitable',
    icon: '✅',
    headline: 'This service covers its costs.',
    detail: 'Looking good! Your price covers your product costs and fees.',
    colour: '#dcfce7',
    textColour: '#14532d',
  };
}

// ─── FORMATTING UTILITIES ─────────────────────────────────────────────────────

function formatCurrency(val) {
  if (val === null || val === undefined || isNaN(val)) return '—';
  return '$' + Math.abs(val).toFixed(2);
}

function formatPercent(val) {
  if (val === null || val === undefined || isNaN(val)) return '—';
  return val.toFixed(1) + '%';
}

function formatHourly(val) {
  if (val === null || val === undefined || isNaN(val)) return '—';
  return '$' + val.toFixed(2) + '/hr';
}

// ─── EXPORTS ─────────────────────────────────────────────────────────────────

window.UL_CALC = {
  // Defaults
  PRODUCT_DEFAULTS: UL_PRODUCT_DEFAULTS,
  SERVICE_PRESETS: UL_SERVICE_PRESETS,
  LENGTH_MULTIPLIER: UL_LENGTH_MULTIPLIER,

  // Core Calc functions
  calcCostByMeasurement,
  calcCostByApplications,
  calcMlUsed,
  calcCostPerPiece,
  calcCostBySheet,
  calcGlitterCost,
  calcWeightPerNailCost,
  calcCuticleOilCost,
  calcLabourCost,
  calcVariableFees,

  // Full service calc
  calcServiceResults,
  
  // Diagnostic
  getDiagnostic,

  // Formatters
  formatCurrency,
  formatPercent,
  formatHourly
};
